/**
 * Акаунт на цьому пристрої.
 *
 * Що тут лежить і чому саме тут:
 *
 * - **запис акаунта** (сіль, параметри Argon2id, обгортки) — копія того,
 *   що зберігає сервер. Локальна копія потрібна, щоб застосунок відкривався
 *   офлайн: без неї розблокування вимагало б мережі, а це суперечило б
 *   тому, що Beresta local-first;
 * - **обгортка пристрою** — ключ даних, замкнений невитягуваним ключем
 *   із сейфа ([ADR-0015]). Саме вона дає відкрити вкладку без парольної
 *   фрази ([ADR-0019]).
 *
 * Чого тут **немає**: ані парольної фрази, ані 24 слів, ані самого ключа
 * даних у відкритому вигляді. Ключ даних живе в памʼяті, поки вкладка
 * відкрита, і більше ніде.
 */
import {
  type AccountKeys,
  type AccountRecord,
  type CipherPort,
  decodeAccountRecord,
  encodeAccountRecord,
  recallOnDevice,
  rememberOnDevice,
} from "@beresta/core";
import { ACCOUNT_STORE, idbRequest, openKeyDb } from "./idb.js";

const RECORD_ID = "record";
const WRAP_ID = "device-wrap";
const EMAIL_ID = "email";

/** Що застосунок знає про акаунт до того, як його розблокували. */
export interface LocalAccount {
  readonly record: AccountRecord;
  readonly email: string;
  /** `null`, якщо на цьому пристрої ще не входили або вийшли. */
  readonly deviceWrap: Uint8Array | null;
}

export async function loadAccount(): Promise<LocalAccount | null> {
  const db = await openKeyDb();
  const encoded = await idbRequest<string | undefined>(db, ACCOUNT_STORE, "readonly", (s) =>
    s.get(RECORD_ID),
  );
  if (!encoded) return null;

  const [email, wrap] = await Promise.all([
    idbRequest<string | undefined>(db, ACCOUNT_STORE, "readonly", (s) => s.get(EMAIL_ID)),
    idbRequest<Uint8Array | undefined>(db, ACCOUNT_STORE, "readonly", (s) => s.get(WRAP_ID)),
  ]);

  return {
    record: decodeAccountRecord(encoded),
    email: email ?? "",
    deviceWrap: wrap ?? null,
  };
}

/**
 * Запис акаунта в тому вигляді, у якому він їде на сервер.
 *
 * `encodeAccountRecord` навмисно відмовляється покласти сюди обгортку
 * пристрою — вона зберігається окремим ключем нижче й на сервер не їде
 * ніколи ([ADR-0016]).
 */
export async function saveAccount(record: AccountRecord, email: string): Promise<void> {
  const db = await openKeyDb();
  await idbRequest(db, ACCOUNT_STORE, "readwrite", (s) =>
    s.put(encodeAccountRecord(record), RECORD_ID),
  );
  await idbRequest(db, ACCOUNT_STORE, "readwrite", (s) => s.put(email, EMAIL_ID));
}

/** Запамʼятати ключ даних на цьому пристрої — далі вкладка відкривається без пароля. */
export async function rememberKeys(cipher: CipherPort, keys: AccountKeys): Promise<void> {
  const db = await openKeyDb();
  const wrap = await rememberOnDevice(cipher, keys);
  await idbRequest(db, ACCOUNT_STORE, "readwrite", (s) => s.put(wrap, WRAP_ID));
}

/**
 * Дістати ключ даних із обгортки пристрою.
 *
 * `null` замість винятку, коли обгортки немає: це звичайний стан першого
 * входу, а не збій. А от обгортка, яка є й не розгортається, — це вже
 * виняток, і ховати його не можна.
 */
export async function recallKeys(
  cipher: CipherPort,
  account: LocalAccount,
): Promise<AccountKeys | null> {
  if (!account.deviceWrap) return null;
  return recallOnDevice(cipher, account.record.accountId, account.deviceWrap);
}

/**
 * Забути ключ даних на цьому пристрої, лишивши сам акаунт.
 *
 * Це і є «вийти» у вебі: запис акаунта лишається, щоб можна було зайти
 * паролем, а обгортка зникає — і без пароля архів більше не відкрити.
 * Оскільки відкрита вкладка не блокується ([ADR-0019]), ця кнопка —
 * єдиний спосіб замкнути архів на спільному компʼютері.
 */
export async function forgetKeys(): Promise<void> {
  const db = await openKeyDb();
  await idbRequest(db, ACCOUNT_STORE, "readwrite", (s) => s.delete(WRAP_ID));
}

const TOKEN_ID = "sync-token";

/**
 * Токен сесії сервера.
 *
 * Живе поруч із обгорткою пристрою, а не в `sync_state`: це облікові
 * дані, і місце їм там, де вже лежить криптоматеріал, а не в таблиці
 * службового стану, яку читає й пише вся синхронізація.
 *
 * Наявність токена **і є** ознакою «синхронізацію ввімкнено». Окремий
 * прапорець став би другим джерелом істини, і перше ж їх розходження
 * дало б застосунок, який вважає, що синхронізується, і мовчить.
 */
export async function saveSyncToken(token: string): Promise<void> {
  const db = await openKeyDb();
  await idbRequest(db, ACCOUNT_STORE, "readwrite", (s) => s.put(token, TOKEN_ID));
}

export async function loadSyncToken(): Promise<string | null> {
  const db = await openKeyDb();
  const token = await idbRequest<string | undefined>(db, ACCOUNT_STORE, "readonly", (s) =>
    s.get(TOKEN_ID),
  );
  return token ?? null;
}

export async function forgetSyncToken(): Promise<void> {
  const db = await openKeyDb();
  await idbRequest(db, ACCOUNT_STORE, "readwrite", (s) => s.delete(TOKEN_ID));
}

const PENDING_REKEY_ID = "pending-rekey";

/**
 * Незавершене переведення архіву на ключ іншого акаунта — [ADR-0026].
 *
 * Існує заради одного: обгортки живуть тут, дані — у SQLite, і атомарними
 * між собою ці два сховища бути не можуть. Порядок «спершу база, потім
 * обгортки» лишає при збої архів під новим ключем і обгортку на старому;
 * зворотний порядок — так само нечитний архів.
 *
 * Тому запис кладеться **до** транзакції й тримає обидві обгортки. Після
 * обриву старт порівнює `sync_state['accountId']` із тим, що тут: збігся —
 * транзакція пройшла, лишилося доробити; ні — вона відкотилася, і старий
 * стан цілий.
 */
export interface PendingRekey {
  /** Запис нового акаунта у формі `encodeAccountRecord`. */
  readonly record: string;
  readonly accountId: string;
  readonly email: string;
  readonly token: string;
  readonly deviceId: string;
  /** Ключ даних нового акаунта під сейфом цього пристрою. */
  readonly newWrap: Uint8Array;
}

export async function loadPendingRekey(): Promise<PendingRekey | null> {
  const db = await openKeyDb();
  const found = await idbRequest<PendingRekey | undefined>(db, ACCOUNT_STORE, "readonly", (s) =>
    s.get(PENDING_REKEY_ID),
  );
  return found ?? null;
}

/**
 * Решта роботи із записом про перехід живе в `sync/rekey.ts`.
 *
 * Тут лишилося тільки читання, бо його робить **старт**, а решту — сам
 * перехід, який трапляється раз на життя пристрою й вантажиться ліниво.
 * Тримати його поруч було зручніше на вигляд і коштувало десяток байтів
 * у холодному старті, де їх немає.
 */
export const PENDING_REKEY_KEY = PENDING_REKEY_ID;
export const DEVICE_WRAP_KEY = WRAP_ID;
